import React from 'react';
import { ListBase, ListView, FunctionField, TextInput, useTranslate, useRefresh } from 'react-admin';
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ListScreen, PageTitle, ResponsiveDatagrid, SectionHeader, StatusDot, useListExport,
} from '@/components/shell';

interface ServerRecord {
  id: number | string;
  name: string;
  port: number;
  url: string;
  status: string;
  isForcedOffline: boolean;
}

const ServerName: React.FC<{ record: ServerRecord }> = ({ record }) => {
  const t = useTranslate();
  return (
    <Box component="span" sx={{ display: 'block' }}>
      <Typography variant="body2" component="span" sx={{ display: 'block' }}>{record.name}</Typography>
      <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
        {t('screens.servers.portLine', { port: record.port })}
      </Typography>
    </Box>
  );
};

/** Reachable or not as a dot and a word; a server taken offline by hand is the exception, so it gets the chip. */
const ServerStatus: React.FC<{ record: ServerRecord }> = ({ record }) => {
  const t = useTranslate();
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <StatusDot
        tone={record.status === 'Online' ? 'ok' : 'problem'}
        label={t(`screens.servers.status.${record.status}`, { _: record.status })}
      />
      {record.isForcedOffline && <Chip label={t('screens.servers.forcedOffline')} color="warning" size="small" />}
    </Stack>
  );
};

const ServerUrl: React.FC<{ record: ServerRecord }> = ({ record }) => (
  <Link href={record.url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} sx={{ overflowWrap: 'anywhere' }}>
    {record.url}
  </Link>
);

/** Rendered inside the list's context so Export sends what the list shows. */
const Header: React.FC = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const exportList = useListExport();

  return (
    <SectionHeader
      left={
        <PageTitle title={t('resources.servers.name', { smart_count: 2 })} subtitle={t('screens.servers.subtitle')} />
      }
      onRefresh={refresh}
      export={{ label: t('ra.action.export'), ...exportList }}
      create={{ label: t('common.create'), onClick: () => navigate('/servers/create') }}
    />
  );
};

export const ServerList = () => {
  const t = useTranslate();
  const navigate = useNavigate();

  const filters = [<TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />];

  return (
    <ListBase perPage={10}>
      <ListScreen header={<Header />}>
        <ListView filters={filters} actions={false} empty={false}>
          <ResponsiveDatagrid<ServerRecord>
            rowClick="edit"
            mobile={{
              primary: (r) => r.name,
              status: (r) => <ServerStatus record={r} />,
              lines: [
                // Plain text on a phone: the card is the tap target, and a one-line link inside it is too small to aim at.
                { label: t('resources.servers.fields.url'), render: (r) => r.url },
                { label: t('resources.servers.fields.port'), render: (r) => r.port },
              ],
              onClick: (r) => navigate(`/servers/${r.id}`),
            }}
          >
            <FunctionField
              label={t('screens.servers.server')}
              sortBy="name"
              render={(r: ServerRecord) => <ServerName record={r} />}
            />
            <FunctionField
              label={t('resources.servers.fields.url')}
              sortBy="url"
              render={(r: ServerRecord) => <ServerUrl record={r} />}
            />
            <FunctionField
              label={t('resources.servers.fields.status')}
              sortBy="status"
              render={(r: ServerRecord) => <ServerStatus record={r} />}
            />
          </ResponsiveDatagrid>
        </ListView>
      </ListScreen>
    </ListBase>
  );
};
